requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    const {
      UrlService
    } = window.ProductListsServices || {};
    const SECTION_TAG = 'product-lists-section';
    const HIGHLIGHT_CLASS = 'is-compare-hide-duplicate-values';

    const qs = (sel, root = document) => root.querySelector(sel);
    const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

    class CompareStickyPanel extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;
        this._boundSchedule = this._boundSchedule || this.scheduleVisibility.bind(this);
        this._boundRecalc = this._boundRecalc || (() => {
          this._container = null;
          this._visibilityTarget = null;
          this.teardownScrollers();
          this.scheduleVisibility();
        });
        this._boundMainScroll = this._boundMainScroll || this.handleMainScroll.bind(this);
        this._boundStickyScroll = this._boundStickyScroll || this.handleStickyScroll.bind(this);
        this.scheduleVisibility();
        window.addEventListener('scroll', this._boundSchedule, {
          passive: true
        });
        window.addEventListener('resize', this._boundSchedule);
        window.addEventListener('orientationchange', this._boundSchedule);
        document.addEventListener('change', this._boundSchedule, true);
        document.addEventListener('shopify:section:load', this._boundRecalc);
      }

      disconnectedCallback() {
        this._connected = false;
        window.removeEventListener('scroll', this._boundSchedule);
        window.removeEventListener('resize', this._boundSchedule);
        window.removeEventListener('orientationchange', this._boundSchedule);
        document.removeEventListener('change', this._boundSchedule, true);
        document.removeEventListener('shopify:section:load', this._boundRecalc);
        this.teardownScrollers();
        if (this._resizeObs) {
          try {
            this._resizeObs.disconnect();
          } catch {}
          this._resizeObs = null;
          this._resizeTarget = null;
        }
        if (this._raf) {
          cancelAnimationFrame(this._raf);
          this._raf = null;
        }
      }

      scheduleVisibility() {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.updateVisibility();
        });
      }

      updateVisibility() {
        const container = this.resolveContainer();
        const visibilityTarget = this.resolveVisibilityTarget(container) || container;
        this.ensureScrollers(container);
        if (!visibilityTarget || visibilityTarget.offsetParent === null) {
          this.classList.remove('compare-sticky-panel--visible');
          return;
        }
        const rect = visibilityTarget.getBoundingClientRect();
        const show = rect.top < 0 && rect.bottom > 0;
        this.classList.toggle('compare-sticky-panel--visible', show);
        document.body.classList.toggle('compare-sticky-panel-visible', show);
      }

      resolveContainer() {
        if (this._container && document.body.contains(this._container)) return this._container;
        const group = this.closest('.compare__group');
        this._container = group ? qs('.compare__table-container', group) : null;
        this.observeContainer();
        return this._container;
      }

      observeContainer() {
        if (this._resizeTarget === this._container) return;
        if (this._resizeObs) {
          try {
            this._resizeObs.disconnect();
          } catch {}
          this._resizeObs = null;
          this._resizeTarget = null;
        }
        if (!this._container || typeof ResizeObserver === 'undefined') return;
        this._resizeObs = new ResizeObserver(() => this.scheduleVisibility());
        this._resizeObs.observe(this._container);
        this._resizeTarget = this._container;
      }

      resolveVisibilityTarget(container) {
        if (this._visibilityTarget && document.body.contains(this._visibilityTarget)) return this._visibilityTarget;
        if (!container) {
          this._visibilityTarget = null;
          return null;
        }
        this._visibilityTarget = qs('tbody', container) || null;
        return this._visibilityTarget;
      }

      ensureScrollers(container) {
        const main = container ? (qs('.compare__scroller--main', container) || qs('.compare__scroller', container)) : null;
        const sticky = qs('.compare__scroller--sticky', this) || qs('.compare__scroller', this);
        if (main === this._mainScroller && sticky === this._stickyScroller) return;
        this.teardownScrollers();
        this._mainScroller = main;
        this._stickyScroller = sticky;
        if (this._mainScroller && this._stickyScroller) {
          this._mainScroller.addEventListener('scroll', this._boundMainScroll, {
            passive: true
          });
          this._stickyScroller.addEventListener('scroll', this._boundStickyScroll, {
            passive: true
          });
          this.syncScroll(this._mainScroller, this._stickyScroller);
        }
      }

      teardownScrollers() {
        if (this._mainScroller) {
          this._mainScroller.removeEventListener('scroll', this._boundMainScroll);
        }
        if (this._stickyScroller) {
          this._stickyScroller.removeEventListener('scroll', this._boundStickyScroll);
        }
        this._mainScroller = null;
        this._stickyScroller = null;
        this._scrollLock = null;
      }

      handleMainScroll() {
        if (!this._mainScroller || !this._stickyScroller) return;
        if (this._scrollLock === this._mainScroller) {
          this._scrollLock = null;
          return;
        }
        this.syncScroll(this._mainScroller, this._stickyScroller);
      }

      handleStickyScroll() {
        if (!this._mainScroller || !this._stickyScroller) return;
        if (this._scrollLock === this._stickyScroller) {
          this._scrollLock = null;
          return;
        }
        this.syncScroll(this._stickyScroller, this._mainScroller);
      }

      syncScroll(source, target) {
        if (!source || !target) return;
        const next = source.scrollLeft;
        if (Math.abs((target.scrollLeft || 0) - next) <= 0.5) return;
        this._scrollLock = target;
        target.scrollLeft = next;
      }
    }
    if (!customElements.get('compare-sticky-panel')) customElements.define('compare-sticky-panel', CompareStickyPanel);

    async function fetchSectionHTML(sectionId) {
      const url = new URL(location.href);
      url.searchParams.set('section_id', sectionId);
      const res = await fetch(url.toString(), {
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('Section fetch failed');
      return res.text();
    }

    function detectPublicMode(mode) {
      return !!UrlService?.hasPublicParam?.(mode || 'compare');
    }

    const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();

    const Registry = {
      items: new Set(),
      _pending: new Set(),
      _timer: null,
      add(x) {
        this.items.add(x);
      },
      del(x) {
        this.items.delete(x);
      },
      scheduleReload(listName) {
        if (!listName) return;
        this._pending.add(listName);
        if (this._timer) return;
        this._timer = setTimeout(async () => {
          const targets = Array.from(this.items).filter(x => this._pending.has(x.mode));
          this._pending.clear();
          this._timer = null;
          await Promise.all(targets.map(x => x.reload().catch(() => {})));
        }, 120);
      }
    };

    class ProductListsSection {
      constructor(root) {
        this.root = root;
        this.mode = (root.getAttribute('mode') || root.dataset.mode || 'compare').toLowerCase();
        this.sectionId = root.dataset.sectionId || null;
        this.publicMode = detectPublicMode(this.mode);
        Registry.add(this);
        this.init();
      }

      init() {
        this.applyPublicView();
        this.updateHideDuplicateValues();

        const mo = new MutationObserver(list => {
          for (const m of list)
            if (m.type === 'attributes' && m.attributeName === 'class') this.updateHideDuplicateValues();
        });
        mo.observe(this.root, {
          attributes: true,
          attributeFilter: ['class']
        });
        this._classObserver = mo;
      }

      applyPublicView() {
        if (!this.publicMode) return;
        qsa('product-lists-remove-button, product-lists-clear-all-button, compare-clear-group-button', this.root)
          .forEach(el => {
            el.setAttribute('hidden', 'hidden');
            el.setAttribute('aria-hidden', 'true');
          });
        const addAll = qs('product-lists-import-shared-button', this.root);
        if (addAll) {
          addAll.removeAttribute('hidden');
          addAll.removeAttribute('aria-hidden');
        }
      }

      updateHideDuplicateValues() {
        const enabled = this.root.classList.contains(HIGHLIGHT_CLASS);
        qsa('.compare-table tr.hidden', this.root).forEach(tr => {
          tr.classList.remove('hidden');
          const mobileRow = tr.previousElementSibling;
          if (mobileRow?.classList?.contains('compare-table__row--mobile')) mobileRow.classList.remove('hidden');
        });
        if (!enabled) return;
        qsa('.compare-table', this.root).forEach(table => {
          qsa('tbody tr[data-row-kind="prop"]', table).forEach(tr => {
            const values = qsa('td[data-col-kind="value"]', tr).map(td => norm(td.textContent));
            if (values.length > 1 && values.every(v => v === values[0])) {
              tr.classList.add('hidden');
              const mobileRow = tr.previousElementSibling;
              if (mobileRow?.classList?.contains('compare-table__row--mobile')) mobileRow.classList.add('hidden');
            }
          });
        });
      }

      async reload() {
        if (!this.sectionId) return;
        const scroller = qs('.compare__scroller, .section-inner, .compare__wrapper', this.root) || this.root;
        const scrollLeft = scroller.scrollLeft || 0;
        const html = await fetchSectionHTML(this.sectionId);
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        const fresh = tpl.content.querySelector(`${SECTION_TAG}[data-section-id="${this.sectionId}"]`);
        if (!fresh) return;
        this.root.replaceWith(fresh);
        this.destroy();
        new ProductListsSection(fresh);
        const newScroller = qs('.compare__scroller, .section-inner, .compare__wrapper', fresh) || fresh;
        newScroller.scrollLeft = scrollLeft;
      }

      destroy() {
        try {
          this._stickyObs?.disconnect();
        } catch {}
        try {
          this._classObserver?.disconnect();
        } catch {}
        Registry.del(this);
      }
    }

    function boot() {
      document.querySelectorAll(SECTION_TAG).forEach(n => {
        if (!n.__compareBooted) n.__compareBooted = new ProductListsSection(n);
      });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.addEventListener('shopify:section:load', boot);
    document.addEventListener('shopify:section:select', boot);

    document.addEventListener('product-lists:change', (e) => {
      const d = e.detail || {};
      const list = (d.list || d.listName || 'compare').toLowerCase();
      if (!/^(add|remove|clear|import:append|import:replace|sync|restore:ls|replace:csv|remove:group|nochange:add|nochange:remove|nochange:clear)$/.test(String(d.type || d.source || ''))) return;
      if (list !== 'compare') return;
      Registry.scheduleReload('compare');
    });
  });
});